import React, { useState, useEffect, useCallback } from 'react';
import { useReservation } from '../../Common/Context/ReserveContextV2';
import { useAlert } from '../../Common/Context/AlertContext';

// 現在の日付をYYYYMMDD形式で取得する関数
const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
};

// YYYYMMDDHH形式の日付を「何月何日 HH:MM」に変換する関数
const formatDateDisplay = (dateString, todayDate) => {
    const year = dateString.slice(0, 4);
    const month = dateString.slice(4, 6);
    const day = dateString.slice(6, 8);
    const hour = dateString.slice(8, 10);
    const formattedDate = `${parseInt(month, 10)}月${parseInt(day, 10)}日 ${hour}:00`;

    return dateString.slice(0, 8) === todayDate ? `即アポ ${hour}:00` : formattedDate;
};

const DateList = ({ date_list, time_list, onDateSelect, resetSelectedDate, tempSelectedDate }) => {
    const { isReserved } = useReservation();
    const [selectedDateDisplay, setSelectedDateDisplay] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const { showAlert } = useAlert();
    const todayDate = getTodayDate();

    // 予約がない場合に選択された日付と時間をリセット
    useEffect(() => {
        if (!isReserved) {
            setSelectedDateDisplay('');
            setSelectedDate(null);
            setSelectedTime(null);
        }
    }, [isReserved]);

    // 初期選択された日付を表示するためのエフェクト
    useEffect(() => {
        if (tempSelectedDate) {
            const dateKey = tempSelectedDate.slice(0, 8);
            const timeKey = `time_${tempSelectedDate.slice(8)}`;
            if (date_list[dateKey] && time_list[timeKey]) {
                const dateDisplay = `${date_list[dateKey].target_date_display} ${time_list[timeKey]}`;
                setSelectedDateDisplay(dateDisplay);
                setSelectedDate(dateKey);  // 初期選択された日付を設定
                setSelectedTime(tempSelectedDate.slice(8));  // 初期選択された時間を設定
            } else {
                const formattedDateDisplay = formatDateDisplay(tempSelectedDate, todayDate);
                setSelectedDateDisplay(formattedDateDisplay);
            }
        }
    }, [tempSelectedDate, date_list, time_list, todayDate]);

    // 日付をクリックしたときの処理
    const handleClick = useCallback((dateDisplay, dateValue, timeKey, dateKey) => {
        if (isReserved) {
            showAlert('他の日付を選択する前に、現在の予約を取り消してください。');
            return;
        }

        if (date_list[dateKey].time_values[timeKey] === 0) {
            showAlert('予約が締め切られています');
            return;
        }

        if (selectedDate === dateKey && selectedTime === timeKey) {
            setSelectedDate(null);
            setSelectedTime(null);
            setSelectedDateDisplay('');
            if (onDateSelect) {
                onDateSelect(null);
            }
            return;
        }

        const correctedTimeKey = timeKey.replace('value', 'time');
        const timeValue = time_list[correctedTimeKey];
        if (!timeValue) {
            console.error(`時間が見つかりません: ${correctedTimeKey}`);
            return;
        }

        const hour = timeValue.split(':')[0].padStart(2, '0'); // 時間を取得して0埋めする
        const appointmentDate = `${dateValue}${hour}`; // 正しい時間を組み込む
        if (onDateSelect) {
            onDateSelect(appointmentDate);
        }

        const dateDisplayFormat = `${dateDisplay} ${timeValue}`;
        setSelectedDateDisplay(dateDisplayFormat);
        setSelectedDate(dateKey);
        setSelectedTime(timeKey);
    }, [isReserved, date_list, time_list, onDateSelect, selectedDate, selectedTime, showAlert]);

    if (Object.keys(date_list).length === 0) {
        return <p>カレンダーが読み込めません。カレンダーが設定されていない可能性があるため、SVまで。</p>;
    }

    return (
        <div className='dateList wrapper'>
            <div className='dateListBox'>
                <div className='flexB'>
                    <p className='timeListbox'>時間</p>
                    {Object.keys(time_list).map((timeKey, index) => (
                        <p key={index} className='timeListLine'>{time_list[timeKey]}</p>
                    ))}
                </div>

                {Object.keys(date_list).map((dateKey) => (
                    <ul key={dateKey} className="flexB">
                        <h2 className={`dateList ${date_list[dateKey].target_date_display.includes('即アポ') ? 'immediateAppo' : 'normalDate'} ${
                            date_list[dateKey].target_date_display.includes('(土)') ? 'saturday' : 
                            date_list[dateKey].target_date_display.includes('(日)') ? 'sunday' : ''
                        }`}>
                            {date_list[dateKey].target_date_display}
                        </h2>

                        {Object.keys(date_list[dateKey].time_values).map((timeKey) => (
                            <li key={timeKey}
                            onClick={() => handleClick(date_list[dateKey].target_date_display, date_list[dateKey].target_date_value, timeKey, dateKey)}
                            style={{
                                background: selectedDate === dateKey && selectedTime === timeKey ? '#ff8f0ccf' :
                                    (date_list[dateKey].target_date_display.includes('即アポ') ? '#ffd2c8' : 'aliceblue'),
                                }}
                            >
                                {date_list[dateKey].time_values[timeKey] === 0 ? (
                                    <span style={{ color: date_list[dateKey].target_date_display.includes('即アポ') ? '#d30000' : '#d30000' }}>締切</span>
                                ) : (
                                    <span>残{date_list[dateKey].time_values[timeKey]}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                ))}
            </div>

            <div className='DateBtnWrapper'>
                {selectedDateDisplay && <p className='timeDisplay'>予約日時: {selectedDateDisplay}</p>}
            </div>
        </div>
    );
};

export default DateList;
